import { ProductImage } from 'microshop-api';

// The withdrawal endpoints are not part of the generated client (generated-sources/microshop) yet.
// These hand-written types and calls mirror the shared DTOs; replace them with the generated
// WithdrawalApi after `npm run api-local`.
export type WithdrawalOrderLine = {
    lineId: string;
    sku: string;
    productName: string;
    size?: string | null;
    color?: string | null;
    split?: string | null;
    image?: ProductImage | null;
    quantity: number;
    unitPrice?: number | null;
    unitPriceFormatted?: string | null;
    lineTotal?: number | null;
    lineTotalFormatted?: string | null;
};

export type WithdrawalOrder = {
    orderNumber: string;
    orderDate: string;
    currency?: string | null;
    totalFormatted?: string | null;
    lines: WithdrawalOrderLine[];
};

export type WithdrawalLookupResponse = {
    order: WithdrawalOrder;
    isWithinWindow: boolean;
    windowEndsAt: string;
};

export type WithdrawalAckLine = {
    lineId: string;
    sku: string;
    productName: string;
    size?: string | null;
    color?: string | null;
    split?: string | null;
    quantity: number;
    unitPriceFormatted?: string | null;
    lineTotalFormatted?: string | null;
};

export type WithdrawalConfirmResponse = {
    orderNumber: string;
    currency?: string | null;
    indicativeTotal?: number | null;
    indicativeTotalFormatted?: string | null;
    submittedAt: string;
    submittedAtFormatted: string;
    lines: WithdrawalAckLine[];
};

export type WithdrawalLookupRequest = { orderNumber: string; email: string };
export type WithdrawalSelectionItem = { lineId: string; sku: string; quantity: number };
export type WithdrawalConfirmRequest = WithdrawalLookupRequest & { selections: WithdrawalSelectionItem[] };

export type WithdrawalResult<T> = { ok: true; data: T } | { ok: false; status: number };

// Anonymous, same-origin calls. No token, shop or unit header is sent: the server resolves the shop
// from the host, and the order number and e-mail travel in the body, never in the URL.
const post = async <T>(path: string, body: unknown): Promise<WithdrawalResult<T>> => {
    try {
        const response = await fetch(path, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!response.ok) return { ok: false, status: response.status };
        return { ok: true, data: (await response.json()) as T };
    } catch {
        return { ok: false, status: 0 };
    }
};

const withdrawal = {
    lookup: (request: WithdrawalLookupRequest) => post<WithdrawalLookupResponse>('/api/withdrawal/lookup', request),
    confirm: (request: WithdrawalConfirmRequest) => post<WithdrawalConfirmResponse>('/api/withdrawal/confirm', request),
};

export { withdrawal };
